import React, { useState, useEffect } from 'react';
import { Pet } from '../types/pets';
import './PetForm.css';

interface PetFormProps {
  pet: Pet | null;
  onSave: (petData: Partial<Pet>) => void;
  onCancel: () => void;
}

const dogBreeds = [
  // Purebred dogs
  "Labrador Retriever", "German Shepherd", "Golden Retriever", "Bulldog", "Beagle", 
  "Poodle", "Rottweiler", "Yorkshire Terrier", "Boxer", "Dachshund", "Siberian Husky",
  "Great Dane", "Doberman Pinscher", "Australian Shepherd", "Shih Tzu", "Pomeranian", 
  "Cocker Spaniel", "Chihuahua", "Mastiff", "Border Collie", "Pit Bull", 
  "Cavalier King Charles Spaniel", "Miniature Schnauzer", "French Bulldog", 
  "Bichon Frise", "Bernese Mountain Dog", "Pug", "Maltese", "Boston Terrier", "Akita",
  "Samoyed", "Havanese", "Weimaraner", "Collie", "Bloodhound", "Newfoundland", 
  "Shiba Inu", "Irish Setter", "Greyhound", "Staffordshire Bull Terrier", 
  "West Highland White Terrier", "Bullmastiff", "St. Bernard", "Alaskan Malamute", 
  "Papillon", "English Springer Spaniel", "Australian Cattle Dog", "Pointer", 
  "Vizsla", "Whippet", "Basset Hound", "Jack Russell Terrier", "Scottish Terrier", 
  "Airedale Terrier", "Belgian Malinois", "Chinese Shar-Pei", "Afghan Hound", 
  "Lhasa Apso", "Cane Corso", "Chow Chow", "Basenji", "Brittany Spaniel", 
  "Cairn Terrier", "Irish Wolfhound", "Old English Sheepdog", 
  "Soft Coated Wheaten Terrier", "Rhodesian Ridgeback", "American Eskimo Dog", 
  "Norwegian Elkhound", "Dalmatian", "Kuvasz", "Great Pyrenees", "Italian Greyhound", 
  "Toy Poodle", "Standard Schnauzer", "Japanese Chin", "Flat-Coated Retriever", 
  "Norwich Terrier", "Miniature Pinscher", "Belgian Tervuren", "Keeshond", 
  "Wirehaired Pointing Griffon", "Clumber Spaniel", "Tibetan Mastiff", "Saluki",

  // Popular mixed breeds
  "Labrador x Poodle (Labradoodle)", "Golden Retriever x Poodle (Goldendoodle)", 
  "Beagle x Pug (Puggle)", "German Shepherd x Husky (Gerberian Shepsky)", 
  "Cocker Spaniel x Poodle (Cockapoo)", "Maltese x Poodle (Maltipoo)", 
  "Yorkshire Terrier x Poodle (Yorkipoo)", "Australian Shepherd x Poodle (Aussiedoodle)", 
  "Border Collie x Australian Shepherd", "Husky x Pomeranian (Pomsky)", 
  "Boxer x Labrador (Boxador)", "Chihuahua x Dachshund (Chiweenie)", 
  "Pit Bull x Labrador (Pitador)", "Shih Tzu x Poodle (Shih-Poo)", 
  "Pomeranian x Chihuahua (Pomchi)", "Poodle x Cavalier King Charles Spaniel (Cavapoo)", 
  "Labrador x Husky (Labsky)", "Corgi x Husky (Horgi)", "Bulldog x Boxer (Bullboxer)", 
  "German Shepherd x Labrador (Sheprador)", "Jack Russell Terrier x Chihuahua (Jack Chi)", 
  "Great Dane x Mastiff (Daniff)", "Rottweiler x Husky (Rottsky)", 
  "Poodle x Schnauzer (Schnoodle)", "Dachshund x Pug (Daug)", 
  "Boston Terrier x French Bulldog (Frenchton)", "Mixed", "Other"
];

const PetForm: React.FC<PetFormProps> = ({ pet, onSave, onCancel }) => {
  const [formData, setFormData] = useState<Partial<Pet>>({
    name: '',
    dob: '',
    breed: '',
    weight: 0,
    life_stage: 'adult',
    activity_level: 'typical',
    body_condition: 'aboutRight',
    percent_kibble: 75,
    includeInFamilyFeedingPlan: true,
    sex: undefined // Default to undefined for backward compatibility
  });

  const [filteredBreeds, setFilteredBreeds] = useState<string[]>([]);

  useEffect(() => {
    if (pet) {
      setFormData(pet);
    }
  }, [pet]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (name === 'breed') {
      const filtered = dogBreeds.filter(breed => 
        breed.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredBreeds(filtered);
    }

    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: checkbox.checked
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    onSave({
      ...formData,
      includeInFamilyFeedingPlan: formData.includeInFamilyFeedingPlan ?? true // Use nullish coalescing
    });
  };

  const handlePhotoClick = () => {
    document.getElementById('pet-photo-upload')?.click();
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = document.getElementById('photo-preview') as HTMLImageElement;
        const addPhotoText = document.querySelector('.add-photo-text') as HTMLElement;
        if (preview && addPhotoText && e.target?.result) {
          preview.src = e.target.result as string;
          preview.style.display = 'block';
          addPhotoText.style.display = 'none';
        }
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <dialog open className="pet-form-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{pet ? 'Edit Dog' : 'Add Dog'}</h2>
          <button type="button" className="close-modal" onClick={onCancel}>&times;</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <input type="hidden" name="id" value={pet?.id} />
          
          <div className="pet-photo-section">
            <div className="photo-placeholder" onClick={handlePhotoClick}>
              {pet?.photo_url ? (
                <img 
                  id="photo-preview" 
                  src={pet.photo_url} 
                  alt={pet.name || 'Pet photo'} 
                  width="120"
                  height="120"
                />
              ) : (
                <>
                  <img 
                    id="photo-preview" 
                    alt="" 
                    style={{ display: 'none' }}
                    width="120"
                    height="120"
                  />
                  <div className="add-photo-text">
                    Add Photo
                  </div>
                </>
              )}
            </div>
            <input 
              type="file" 
              id="pet-photo-upload" 
              accept="image/*" 
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="pet-name">Name:</label>
            <input type="text" id="pet-name" name="name" defaultValue={pet?.name} required />
          </div>

          <div className="form-group">
            <label htmlFor="pet-dob">Date of Birth:</label>
            <input type="date" id="pet-dob" name="dob" defaultValue={pet?.dob} required />
          </div>

          <div className="form-group">
            <label htmlFor="breed">Breed</label>
            <input
              type="text"
              id="breed"
              name="breed"
              list="breed-list"
              value={formData.breed}
              onChange={handleChange}
              required
            />
            <datalist id="breed-list">
              {filteredBreeds.map((breed, index) => (
                <option key={index} value={breed} />
              ))}
            </datalist>
          </div>

          <div className="form-group">
            <label htmlFor="weight">Weight (kg)</label>
            <input
              type="number"
              id="weight"
              name="weight"
              value={formData.weight}
              onChange={handleChange}
              step="0.1"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="life_stage">Life Stage</label>
            <select
              id="life_stage"
              name="life_stage"
              value={formData.life_stage}
              onChange={handleChange}
              required
            >
              <option value="puppy">Puppy</option>
              <option value="adult">Adult</option>
              <option value="senior">Senior</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="activity_level">Activity Level</label>
            <select
              id="activity_level"
              name="activity_level"
              value={formData.activity_level}
              onChange={handleChange}
              required
            >
              <option value="lazyBones">Lazy Bones</option>
              <option value="typical">Typical</option>
              <option value="active">Active</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="body_condition">Body Condition</label>
            <select
              id="body_condition"
              name="body_condition"
              value={formData.body_condition}
              onChange={handleChange}
              required
            >
              <option value="underweight">Underweight</option>
              <option value="aboutRight">About Right</option>
              <option value="overweight">Overweight</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="percent_kibble">FMFB Percentage</label>
            <input
              type="number"
              id="percent_kibble"
              name="percent_kibble"
              value={formData.percent_kibble}
              onChange={handleChange}
              min="0"
              max="100"
              required
            />
          </div>

          <div className="form-group">
            <label>Sex:</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="sex"
                  value="male"
                  checked={formData.sex === 'male'}
                  onChange={handleChange}
                />
                Male
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="sex"
                  value="female"
                  checked={formData.sex === 'female'}
                  onChange={handleChange}
                />
                Female
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="includeInFamilyFeedingPlan"
                checked={formData.includeInFamilyFeedingPlan}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    includeInFamilyFeedingPlan: e.target.checked
                  }));
                }}
              />
              Include in Family Feeding Plan
            </label>
          </div>

          <div className="modal-footer">
            <button type="submit" className="button button--primary">Save Pet</button>
            <button type="button" className="button button--secondary" onClick={onCancel}>Cancel</button>
          </div>
        </form>
      </div>
    </dialog>
  );
};

export default PetForm; 